import { supabase } from '@tickitacka/shared';
import { Team } from '@/common/types/databaseTypes';
import { v4 as uuidv4 } from 'uuid';
import { authService } from '@/features/auth/services/authService';

export const teamService = {
    async getTeamById(teamId: string): Promise<Team> {
        try {
            const { data, error } = await supabase
                .from('team')
                .select('*')
                .eq('id', teamId)
                .eq('enable', true)
                .maybeSingle();

            if (error) throw error;
            if (!data) throw new Error(`Team not found with id: ${teamId}`);

            return data;
        } catch (error) {
            console.error(`Failed to fetch team ${teamId}`, error);
            throw error;
        }
    },

    async createTeam(
        name: string,
        workspaceId: string,
        description?: string,
        profileImageUrl?: string
    ): Promise<Team> {
        try {
            const result = await supabase
                .from('team')
                .insert({
                    id: uuidv4(), // 고유 ID 생성
                    name,
                    description,
                    profile_image_url: profileImageUrl,
                    register_id:
                        (await supabase.auth.getUser()).data.user?.id || '', // 현재 로그인한 사용자 ID
                    register_date: new Date().toISOString(), // 현재 날짜
                    enable: true, // 기본적으로 활성화
                    workspace_id: workspaceId,
                })
                .select();

            if (result.error) {
                throw new Error(`팀 생성 실패: ${result.error.message}`);
            }

            if (!result.data || result.data.length === 0) {
                throw new Error('팀 생성 후 데이터를 찾을 수 없습니다.');
            }

            const team: Team = result.data[0];

            console.log('TeamService.createTeam 성공:', team);
            return team;
        } catch (error) {
            console.error('TeamService.createTeam 실패:', error);
            if (error instanceof Error) {
                throw error;
            }
            throw new Error('팀 생성 중 알 수 없는 오류가 발생했습니다.');
        }
    },

    async updateTeam(
        id: string,
        name?: string,
        description?: string,
        profileImageUrl?: string
    ): Promise<Team> {
        console.log('TeamService.updateTeam 시작:', {
            id,
            name,
            description,
            profileImageUrl,
        });
        try {
            const result = await supabase
                .from('team')
                .update({
                    name,
                    description,
                    profile_image_url: profileImageUrl,
                })
                .eq('id', id)
                .select();

            if (result.error) {
                throw new Error(
                    `팀 프로필 업데이트에 실패했습니다. : ${result.error.message}`
                );
            }

            if (!result.data || result.data.length === 0) {
                throw new Error('팀 업데이트 후 데이터를 찾을 수 없습니다.');
            }

            const team: Team = result.data[0];

            console.log('TeamService.updateTeam 성공:', team);
            return team;
        } catch (error) {
            console.error('TeamService.updateTeam 실패:', error);
            if (error instanceof Error) {
                throw error;
            }
            throw new Error('팀 업데이트 중 알 수 없는 오류가 발생했습니다.');
        }
    },

    async deleteTeam(teamId: string): Promise<string> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { error } = await supabase
                .from('team')
                .update({
                    enable: false,
                    update_id: user.id,
                    update_date: new Date().toISOString(),
                })
                .eq('id', teamId);

            if (error) throw error;
            return teamId;
        } catch (error) {
            console.error(`Failed to delete team ${teamId}`, error);
            throw error;
        }
    },

    async getMyTeams(): Promise<Team[]> {
        try {
            const {
                data: { user },
            } = await supabase.auth.getUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { data: teamMembers, error: memberError } = await supabase
                .from('team_member')
                .select('team_id')
                .eq('user_id', user.id)
                .eq('enable', true);
            if (memberError) throw memberError;

            const teamIds = teamMembers.map(member => member.team_id);

            if (teamIds.length === 0) return [];

            const { data: teams, error: teamError } = await supabase
                .from('team')
                .select('*')
                .in('id', teamIds)
                .eq('enable', true);

            if (teamError) throw teamError;

            return teams as Team[];
        } catch (error) {
            console.error('getMyTeams 실패:', error);
            throw new Error('팀 목록을 가져오는데 실패했습니다.');
        }
    },

    // async getTeamsByWorkspaceId(workspaceId: string): Promise<Team[]> {
    //     try {
    //         const { data, error } = await supabase
    //             .from('team')
    //             .select('*')
    //             .eq('workspace_id', workspaceId)
    //             .eq('enable', true);

    //         if (error) throw error;
    //         return data || [];
    //     } catch (error) {
    //         console.error(`Failed to fetch teams by workspace ${workspaceId}`, error);
    //         throw error;
    //     }
    // },

    async getMyTeamsByWorkspaceId(workspaceId: string): Promise<Team[]> {
        const user = await authService.getCurrentUser();
        if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

        try {
            const { data, error } = await supabase
                .from('team')
                .select('*, team_member!inner(*)')
                .eq('workspace_id', workspaceId)
                .eq('enable', true)
                .eq('team_member.user_id', user.id)
                .eq('team_member.enable', true);

            if (error) throw error;
            return data || [];
        } catch (error) {
            console.error(
                `Failed to fetch teams by workspace ${workspaceId}`,
                error
            );
            throw error;
        }
    },

    async updateProfile(
        id: string,
        name: string,
        description?: string,
        profileImageUrl?: string
    ): Promise<Team> {
        console.log('TeamService.updateProfile 시작:', {
            id,
            name,
            description,
            profileImageUrl,
        });
        try {
            const result = await supabase
                .from('team')
                .update({
                    name,
                    description,
                    profile_image_url: profileImageUrl,
                })
                .eq('id', id)
                .select();

            if (result.error) {
                throw new Error(
                    `팀 프로필 업데이트에 실패했습니다. : ${result.error.message}`
                );
            }

            if (!result.data || result.data.length === 0) {
                throw new Error(
                    '팀 프로필 업데이트 후 데이터를 찾을 수 없습니다.'
                );
            }

            const team: Team = result.data[0];

            console.log('TeamService.updateProfile 성공:', team);
            return team;
        } catch (error) {
            console.error('TeamService.updateProfile 실패:', error);
            if (error instanceof Error) {
                throw error;
            }
            throw new Error(
                '팀 프로필 업데이트 중 알 수 없는 오류가 발생했습니다.'
            );
        }
    },
};
